/**
 * Hook usePurchases
 * 
 * Hook React para gerenciar dados de requisições de compra.
 * Fornece funções para buscar, criar, atualizar e deletar requisições.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { purchaseService, reviewService, followUpService } from '../services/purchasesApi'

/**
 * Hook para gerenciar lista de requisições
 */
export function useRequisitions(filters = {}) {
  const [requisitions, setRequisitions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState(null)
  const filtersRef = useRef(filters)

  useEffect(() => {
    filtersRef.current = filters
    
    const fetchRequisitions = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const response = await purchaseService.getAll(filters)
        
        if (response) {
          if (response.success) {
            setRequisitions(response.data || [])
            setPagination(response.pagination || null)
          } else {
            setError(response.message || 'Erro ao buscar requisições')
            setRequisitions([])
          }
        } else {
          setError('Resposta inválida do servidor')
          setRequisitions([])
        }
      } catch (err) {
        console.error('Erro ao buscar requisições:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar requisições'
        setError(errorMessage)
        setRequisitions([])
      } finally {
        setLoading(false)
      }
    }
    
    fetchRequisitions()
  }, [filters])

  const refetch = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await purchaseService.getAll(filtersRef.current)
      if (response.success) {
        setRequisitions(response.data || [])
        setPagination(response.pagination || null)
      } else {
        setError(response.message || 'Erro ao buscar requisições')
      }
    } catch (err) {
      setError(err.message || 'Erro ao buscar requisições')
      setRequisitions([])
    } finally {
      setLoading(false)
    }
  }

  return {
    requisitions,
    loading,
    error,
    pagination,
    refetch
  }
}

/**
 * Hook para gerenciar uma requisição específica
 */
export function useRequisition(id) {
  const [requisition, setRequisition] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRequisition = useCallback(async () => {
    if (!id) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await purchaseService.getById(id)
      if (response.success) {
        setRequisition(response.data)
      } else {
        setError(response.message || 'Erro ao buscar requisição')
      }
    } catch (err) {
      setError(err.message || 'Erro ao buscar requisição')
      setRequisition(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchRequisition()
  }, [fetchRequisition])

  return {
    requisition,
    loading,
    error,
    refetch: fetchRequisition
  }
}

/**
 * Hook para lista de revisão técnica
 */
export function useReviewList() {
  const [requisitions, setRequisitions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchList = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await reviewService.getList()
      if (response.success) {
        setRequisitions(response.data || [])
      } else {
        setError(response.message || 'Erro ao buscar lista de revisão')
      }
    } catch (err) {
      setError(err.message || 'Erro ao buscar lista de revisão')
      setRequisitions([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  return {
    requisitions,
    loading,
    error,
    refetch: fetchList
  }
}

/**
 * Hook para lista de rastreio (follow-up)
 */
export function useFollowUp() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchList = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await followUpService.getList()
      if (response.success) {
        setOrders(response.data || [])
      } else {
        setError(response.message || 'Erro ao buscar lista de rastreio')
      }
    } catch (err) {
      setError(err.message || 'Erro ao buscar lista de rastreio')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  return {
    orders,
    loading,
    error,
    refetch: fetchList
  }
}
